/**
 * CreatureBuilder — modelos PROCEDURAIS estilizados (Fase 8). Compõe silhuetas
 * legíveis a partir de primitivas (corpo, cabeça, patas, cauda, asas, cristais) em vez
 * de "uma caixa vermelha andando". Geometrias compartilhadas por módulo (cache);
 * materiais por instância (flash/guard individuais). Retorna um "rig" com peças
 * nomeadas para animação procedural no Enemy (gait, breathe, lean, hit, death).
 *
 * Todas as criaturas têm origem nos PÉS (y=0) para assentar no chão; `height` orienta
 * a âncora da barra de vida.
 */
import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { buildForestCreature } from "./ForestCreatures";
import { applySkin, type SkinKind } from "./CreatureSkins";
import { enemyRegion, regionLook, type RegionLook } from "./EnemyRegion";
import { applyRim } from "../fx/RimLight";
import { decalTexture } from "../world/GroundTextures";

/**
 * 10.9HK — CARA DO TIER. Um bicho comum de território tier 5 era idêntico ao de tier 1 (só a vida
 * mudava). Agora o tier do território veste a criatura: espinhos (T2+), placas de carapaça (T3+),
 * cristais crescendo nas costas (T4+) e veios luminosos + porte maior (T5).
 */
export function applyTierLook(rig: CreatureRig, tier: number, color: number): void {
  if (tier < 2) return;
  const mat = new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.6 + tier * 0.12, roughness: 0.35, flatShading: true });
  rig.crystalMats.push(mat);
  const h = rig.height;
  const w = Math.max(0.35, h * 0.35);
  const spikeGeo = geo("tierSpike", () => new THREE.ConeGeometry(0.07, 0.3, 4));
  const nSpikes = tier >= 3 ? 4 : 2;
  for (let i = 0; i < nSpikes; i++) {
    const s = new THREE.Mesh(spikeGeo, mat);
    const t = i / (nSpikes - 1) - 0.5;
    s.position.set((i % 2 ? 0.14 : -0.14) * w, h * 0.72, -t * w * 0.8);
    s.rotation.x = -0.4 - t * 0.5;
    s.scale.setScalar(0.8 + tier * 0.12);
    rig.group.add(s);
  }
  if (tier >= 3) {
    // placas de carapaça escuras nas costas
    const plateMat = new THREE.MeshStandardMaterial({ color: 0x1c1a2c, emissive: color, emissiveIntensity: 0.12, roughness: 0.5, metalness: 0.3, flatShading: true });
    rig.crystalMats.push(plateMat);
    const plateGeo = geo("tierPlate", () => new THREE.BoxGeometry(0.34, 0.06, 0.26));
    for (let i = 0; i < (tier >= 4 ? 3 : 2); i++) {
      const p = new THREE.Mesh(plateGeo, plateMat);
      p.position.set(0, h * 0.66, (i - 1) * w * 0.4);
      p.rotation.x = 0.15 * (i - 1);
      p.scale.setScalar(Math.max(0.7, w * 1.4));
      rig.group.add(p);
    }
  }
  if (tier >= 4) {
    // cristais crescendo nas costas
    const cryGeo = geo("tierCrystal", () => new THREE.OctahedronGeometry(0.11, 0));
    for (let i = 0; i < 3; i++) {
      const c = new THREE.Mesh(cryGeo, mat);
      c.position.set((i - 1) * w * 0.35, h * 0.78 + (i === 1 ? 0.1 : 0), -w * 0.2);
      c.scale.set(0.8, 1.7 + (i === 1 ? 0.6 : 0), 0.8);
      c.rotation.z = (i - 1) * 0.35;
      rig.group.add(c);
    }
  }
  if (tier >= 5) {
    applyVeins(rig.bodyMats, color, 0.9);
    rig.group.scale.multiplyScalar(1.06);
  }
}

/** 10.9GV — veios luminosos (rachaduras) no corpo de elites/campeões — textura compartilhada. */
let _veinTex: THREE.Texture | null = null;
function veinTexture(): THREE.Texture {
  if (!_veinTex) {
    _veinTex = decalTexture("crack").clone();
    _veinTex.wrapS = _veinTex.wrapT = THREE.RepeatWrapping;
    _veinTex.repeat.set(2, 2);
    _veinTex.needsUpdate = true;
  }
  return _veinTex;
}
/** Aplica os veios: o emissivo passa a existir só nas rachaduras, na cor da aura. */
export function applyVeins(mats: THREE.MeshStandardMaterial[], color: number, intensity = 1.1): void {
  for (const m of mats) {
    m.emissiveMap = veinTexture();
    m.emissive.setHex(color);
    m.emissiveIntensity = Math.max(m.emissiveIntensity, intensity);
    m.needsUpdate = true;
  }
}
import type { EnemyDef, EnemyShape } from "../config/GameConfig";

export interface CreatureRig {
  group: THREE.Group;
  bodyMats: THREE.MeshStandardMaterial[]; // materiais que recebem flash de dano
  crystalMats: THREE.MeshStandardMaterial[]; // ornamentos energéticos (guard/telegraph)
  legs: THREE.Object3D[]; // pernas/patas para o gait
  wings: THREE.Object3D[]; // asas (flyer) para bater
  head: THREE.Object3D | null;
  tail: THREE.Object3D | null;
  core: THREE.Object3D | null; // corpo central (breathe/squash)
  shield?: THREE.Object3D | null; // Fase 10 (Stormback): bolha de Static Shield (toggle)
  height: number; // topo aproximado (mundo, antes de scale do grupo)
}

// ---- cache de geometrias compartilhadas (perf mobile) ----
const geoCache = new Map<string, THREE.BufferGeometry>();
function geo(key: string, make: () => THREE.BufferGeometry): THREE.BufferGeometry {
  let g = geoCache.get(key);
  if (!g) {
    g = make();
    if (g instanceof THREE.BoxGeometry) {
      const { width, height, depth } = g.parameters;
      g.dispose();
      g = new RoundedBoxGeometry(width, height, depth, 2, Math.min(width, height, depth) * 0.28);
    }
    g.userData.shared = true;
    geoCache.set(key, g);
  }
  return g;
}

function stdMat(color: number, opts?: { emissive?: number; ei?: number; rough?: number; metal?: number }): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    emissive: opts?.emissive ?? color,
    emissiveIntensity: opts?.ei ?? 0.32, // 10.5E: um pouco mais de "pop" sob a luz mais contrastada
    roughness: opts?.rough ?? 0.62,
    metalness: opts?.metal ?? 0.16,
    flatShading: true,
  });
}
function crystalMat(color: number): THREE.MeshStandardMaterial {
  // 10.9BT: 1.6 → 1.2 — com o bloom novo (threshold 0.85) os cristais em repouso estouravam em branco
  return new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1.2, roughness: 0.25, metalness: 0.25, flatShading: true });
}

/** Olhos frontais (leitura de direção; +Z = frente). */
function addEyes(parent: THREE.Object3D, r: number, fz: number, y: number, color = 0xff4d6d, sep = 0.32): void {
  const m = new THREE.MeshBasicMaterial({ color });
  const g = geo(`eye${r.toFixed(2)}`, () => new THREE.SphereGeometry(r, 6, 6));
  for (const sx of [-sep, sep]) {
    const e = new THREE.Mesh(g, m);
    e.position.set(sx, y, fz);
    e.userData.eye = true; // 10.9BT: elite recebe olhos maiores (ver finishCreature)
    parent.add(e);
  }
}

/** Assinatura visual comum: núcleo energético e runa no chão melhoram leitura e presença. */
function addVoidSignature(rig: CreatureRig, def: EnemyDef): void {
  const color = def.crystalColor ?? def.elite?.aura ?? 0x8f6bff;
  const radius = Math.max(0.28, def.radius * 0.24);
  const coreMat = new THREE.MeshBasicMaterial({
    color, transparent: true, opacity: def.elite ? 0.92 : 0.68,
    depthWrite: false, blending: THREE.AdditiveBlending,
  });
  const core = new THREE.Mesh(geo(`voidCore${radius.toFixed(2)}`, () => new THREE.OctahedronGeometry(radius, 0)), coreMat);
  core.position.y = Math.max(def.radius * 1.05, rig.height * 0.52);
  core.scale.set(0.55, 1.15, 0.55);
  rig.group.add(core);

  const runeMat = new THREE.MeshBasicMaterial({
    color, transparent: true, opacity: def.elite ? 0.42 : 0.18,
    depthWrite: false, blending: THREE.AdditiveBlending,
  });
  const rune = new THREE.Mesh(
    geo(`voidRune${def.radius.toFixed(2)}`, () => new THREE.RingGeometry(def.radius * 0.78, def.radius * 0.9, 20)),
    runeMat,
  );
  rune.rotation.x = -Math.PI / 2;
  rune.position.y = 0.045;
  rig.group.add(rune);
}

// ==================== silhuetas ====================

function buildBeast(def: EnemyDef, rig: CreatureRig): void {
  // Shardfang: predador baixo/ágil — corpo alongado, cabeça agressiva, 4 patas, cauda, cristais nas costas
  const r = def.radius;
  const body = stdMat(def.color, { ei: 0.35 });
  rig.bodyMats.push(body);
  const cm = crystalMat(def.crystalColor ?? 0x62e8ff);
  rig.crystalMats.push(cm);

  const core = new THREE.Mesh(geo("beastBody", () => new THREE.BoxGeometry(0.8, 0.55, 1.5)), body);
  core.position.y = r + 0.35;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;

  // pescoço + cabeça em cunha (mandíbula)
  const head = new THREE.Group();
  head.position.set(0, r + 0.55, 0.85);
  const skull = new THREE.Mesh(geo("beastHead", () => new THREE.BoxGeometry(0.5, 0.42, 0.55)), body);
  head.add(skull);
  const jaw = new THREE.Mesh(geo("beastJaw", () => new THREE.ConeGeometry(0.26, 0.5, 4)), body);
  jaw.rotation.x = Math.PI / 2;
  jaw.position.set(0, -0.05, 0.4);
  head.add(jaw);
  addEyes(head, 0.07, 0.28, 0.08, 0x9cff5a, 0.16);
  rig.group.add(head);
  rig.head = head;

  // 10.5N — presas + coleira de cristal (leitura de "predador", não de caixa com pernas)
  const fangGeo = geo("beastFang", () => new THREE.ConeGeometry(0.05, 0.2, 4));
  for (const fx of [-0.11, 0.11]) {
    const f = new THREE.Mesh(fangGeo, body);
    f.position.set(fx, -0.16, 0.5);
    f.rotation.x = Math.PI * 0.92;
    head.add(f);
  }
  const collar = new THREE.Mesh(geo("beastCollar", () => new THREE.TorusGeometry(0.3, 0.06, 6, 12)), cm);
  collar.rotation.x = Math.PI / 2;
  collar.position.set(0, r + 0.5, 0.5);
  rig.group.add(collar);
  // 4 patas com garras
  const legGeo = geo("beastLeg", () => new THREE.BoxGeometry(0.16, 0.5, 0.16));
  const clawGeo = geo("beastClaw", () => new THREE.ConeGeometry(0.06, 0.16, 4));
  for (const [lx, lz] of [[-0.28, 0.5], [0.28, 0.5], [-0.28, -0.55], [0.28, -0.55]]) {
    const leg = new THREE.Mesh(legGeo, body);
    leg.position.set(lx, r + 0.05, lz);
    leg.castShadow = true;
    rig.group.add(leg);
    rig.legs.push(leg);
    const claw = new THREE.Mesh(clawGeo, cm);
    claw.position.set(lx, r - 0.2, lz + 0.1);
    claw.rotation.x = Math.PI / 2;
    rig.group.add(claw);
  }
  // cauda
  const tail = new THREE.Mesh(geo("beastTail", () => new THREE.ConeGeometry(0.14, 0.9, 5)), body);
  tail.rotation.x = -Math.PI / 2.4;
  tail.position.set(0, r + 0.45, -0.95);
  rig.group.add(tail);
  rig.tail = tail;
  // cristais nas costas
  const spikeGeo = geo("beastSpike", () => new THREE.OctahedronGeometry(0.18, 0));
  for (const [sz, sc] of [[0.3, 1], [-0.05, 1.25], [-0.4, 0.9]]) {
    const s = new THREE.Mesh(spikeGeo, cm);
    s.position.set(0, r + 0.75, sz);
    s.scale.setScalar(sc);
    rig.group.add(s);
  }
  rig.height = r + 1.05;
}

function buildHeavy(def: EnemyDef, rig: CreatureRig): void {
  // Craghorn: quadrúpede pesado — corpo massivo, pernas grossas, placas, 2 chifres, ombros largos
  const r = def.radius;
  const body = stdMat(def.color, { ei: 0.2, rough: 0.95 });
  rig.bodyMats.push(body);
  const stone = stdMat(0x8a94a6, { ei: 0.05, rough: 1 });
  rig.bodyMats.push(stone);
  const cm = crystalMat(def.crystalColor ?? 0x8ea2ff);
  rig.crystalMats.push(cm);

  const core = new THREE.Mesh(geo("heavyBody", () => new THREE.BoxGeometry(1.3, 1.0, 1.7)), body);
  core.position.y = r + 0.55;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;
  // placas de pedra nas costas
  const plateGeo = geo("heavyPlate", () => new THREE.BoxGeometry(1.2, 0.25, 0.5));
  for (const pz of [0.4, -0.1, -0.6]) {
    const p = new THREE.Mesh(plateGeo, stone);
    p.position.set(0, r + 1.12, pz);
    p.rotation.x = -0.15;
    rig.group.add(p);
  }
  // cabeça baixa + 2 chifres
  const head = new THREE.Group();
  head.position.set(0, r + 0.55, 1.0);
  const skull = new THREE.Mesh(geo("heavyHead", () => new THREE.BoxGeometry(0.75, 0.6, 0.6)), body);
  head.add(skull);
  const hornGeo = geo("heavyHorn", () => new THREE.ConeGeometry(0.12, 0.6, 5));
  for (const hx of [-0.28, 0.28]) {
    const horn = new THREE.Mesh(hornGeo, stone);
    horn.position.set(hx, 0.4, 0.15);
    horn.rotation.set(-0.5, 0, hx < 0 ? 0.3 : -0.3);
    head.add(horn);
  }
  addEyes(head, 0.08, 0.32, 0.05, 0xffd24a, 0.2);
  rig.group.add(head);
  rig.head = head;
  // 10.5N — focinho/anel nasal e couraça no peito (o Craghorn era um bloco liso)
  const snout = new THREE.Mesh(geo("heavySnout", () => new THREE.BoxGeometry(0.5, 0.34, 0.34)), stone);
  snout.position.set(0, -0.1, 0.4);
  head.add(snout);
  const nose = new THREE.Mesh(geo("heavyNose", () => new THREE.TorusGeometry(0.12, 0.035, 5, 10)), cm);
  nose.position.set(0, -0.18, 0.56);
  head.add(nose);
  const bib = new THREE.Mesh(geo("heavyBib", () => new THREE.BoxGeometry(1.0, 0.5, 0.2)), stone);
  bib.position.set(0, r + 0.5, 0.85);
  bib.rotation.x = 0.25;
  rig.group.add(bib);
  // 4 pernas grossas com casco
  const legGeo = geo("heavyLeg", () => new THREE.BoxGeometry(0.34, 0.62, 0.34));
  const hoofGeo = geo("heavyHoof", () => new THREE.CylinderGeometry(0.22, 0.26, 0.16, 6));
  for (const [lx, lz] of [[-0.5, 0.6], [0.5, 0.6], [-0.5, -0.6], [0.5, -0.6]]) {
    const leg = new THREE.Mesh(legGeo, body);
    leg.position.set(lx, r + 0.02, lz);
    leg.castShadow = true;
    rig.group.add(leg);
    rig.legs.push(leg);
    const hoof = new THREE.Mesh(hoofGeo, stone);
    hoof.position.set(lx, r - 0.28, lz);
    rig.group.add(hoof);
  }
  // cristais no ombro (3, em leque)
  const cryGeo = geo("heavyCry", () => new THREE.OctahedronGeometry(0.26, 0));
  for (const [cx, cy, cz, s] of [[0, 1.35, 0.2, 1], [-0.4, 1.25, -0.1, 0.7], [0.4, 1.25, -0.1, 0.7]] as const) {
    const c = new THREE.Mesh(cryGeo, cm);
    c.position.set(cx, r + cy, cz);
    c.scale.setScalar(s);
    rig.group.add(c);
  }
  rig.height = r + 1.6;
}

function buildFlyer(def: EnemyDef, rig: CreatureRig): void {
  // 10.5N — VOID WISP / Aetherwing: medusa do Void. Casco cristalino em cima, núcleo pulsante
  // dentro de um anel orbital, 4 asas-pétala com nervura e TENTÁCULOS que balançam embaixo.
  const r = def.radius;
  const body = stdMat(def.color, { ei: 0.5, rough: 0.4 });
  rig.bodyMats.push(body);
  const cm = crystalMat(def.crystalColor ?? 0xa9f0ff);
  rig.crystalMats.push(cm);

  const floatY = r + 1.6; // flutua alto
  const core = new THREE.Mesh(geo("flyerBody", () => new THREE.OctahedronGeometry(0.42, 0)), body);
  core.position.y = floatY;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;
  // casco superior (domo) — dá silhueta de "água-viva" em vez de losango solto
  const hood = new THREE.Mesh(geo("flyerHood", () => new THREE.SphereGeometry(0.44, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2)), body);
  hood.position.y = floatY + 0.12;
  hood.scale.set(1.15, 0.85, 1.15);
  hood.castShadow = true;
  rig.group.add(hood);
  // núcleo cristal + anel orbital
  const nucleus = new THREE.Mesh(geo("flyerCore", () => new THREE.IcosahedronGeometry(0.2, 0)), cm);
  nucleus.position.y = floatY;
  rig.group.add(nucleus);
  const ring = new THREE.Mesh(geo("flyerRing", () => new THREE.TorusGeometry(0.52, 0.035, 6, 18)), cm);
  ring.rotation.x = Math.PI / 2.3;
  ring.position.y = floatY;
  rig.group.add(ring);
  rig.wings.push(ring); // o bob das "asas" também gira o anel
  // 4 asas/pétalas de energia com nervura
  const wingGeo = geo("flyerWing", () => new THREE.ConeGeometry(0.3, 1.0, 4));
  const veinGeo = geo("flyerVein", () => new THREE.BoxGeometry(0.03, 0.9, 0.03));
  for (let i = 0; i < 4; i++) {
    const wing = new THREE.Group();
    wing.position.y = floatY;
    wing.rotation.y = (i / 4) * Math.PI * 2;
    const petal = new THREE.Mesh(wingGeo, cm);
    petal.rotation.z = Math.PI / 2;
    petal.rotation.y = Math.PI / 2;
    petal.position.set(0, 0, 0.6);
    wing.add(petal);
    const vein = new THREE.Mesh(veinGeo, body);
    vein.rotation.x = Math.PI / 2;
    vein.position.set(0, 0.02, 0.6);
    wing.add(vein);
    rig.group.add(wing);
    rig.wings.push(wing);
  }
  // tentáculos: cauda central + 4 fios curtos ao redor
  const tail = new THREE.Mesh(geo("flyerTail", () => new THREE.ConeGeometry(0.12, 0.7, 5)), cm);
  tail.position.y = floatY - 0.5;
  rig.group.add(tail);
  rig.tail = tail;
  const tentGeo = geo("flyerTent", () => new THREE.ConeGeometry(0.05, 0.55, 4));
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const t = new THREE.Mesh(tentGeo, cm);
    t.position.set(Math.cos(a) * 0.22, floatY - 0.38, Math.sin(a) * 0.22);
    t.rotation.z = Math.cos(a) * 0.25;
    t.rotation.x = -Math.sin(a) * 0.25;
    rig.group.add(t);
  }
  addEyes(core, 0.06, 0.33, 0.05, 0xffffff, 0.13);
  rig.height = floatY + 0.4;
}

function buildShell(def: EnemyDef, rig: CreatureRig): void {
  // Glintback: defensiva — corpo baixo compacto, carapaça cristalina, placas, patas curtas
  const r = def.radius;
  const body = stdMat(def.color, { ei: 0.2, rough: 0.85 });
  rig.bodyMats.push(body);
  const cm = crystalMat(def.crystalColor ?? 0x76ffd0);
  rig.crystalMats.push(cm);

  const core = new THREE.Mesh(geo("shellBody", () => new THREE.BoxGeometry(1.0, 0.45, 1.1)), body);
  core.position.y = r + 0.1;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;
  // carapaça (domo)
  const dome = new THREE.Mesh(geo("shellDome", () => new THREE.SphereGeometry(0.72, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2)), body);
  dome.position.y = r + 0.28;
  dome.scale.set(1.1, 0.9, 1.2);
  dome.castShadow = true;
  rig.group.add(dome);
  // placas cristalinas na carapaça
  const plateGeo = geo("shellPlate", () => new THREE.OctahedronGeometry(0.2, 0));
  for (const [px, pz] of [[0, 0.1], [-0.35, -0.2], [0.35, -0.2], [0, -0.45]]) {
    const p = new THREE.Mesh(plateGeo, cm);
    p.position.set(px, r + 0.7, pz);
    p.scale.set(1, 0.7, 1);
    rig.group.add(p);
  }
  // cabecinha
  const head = new THREE.Mesh(geo("shellHead", () => new THREE.BoxGeometry(0.34, 0.3, 0.34)), body);
  head.position.set(0, r + 0.2, 0.62);
  rig.group.add(head);
  addEyes(head, 0.06, 0.2, 0.02, 0x9cff5a, 0.11);
  rig.head = head;
  // 10.5N — borda da carapaça (aro) + cauda curta: silhueta de tatu-cristal
  const rim = new THREE.Mesh(geo("shellRim", () => new THREE.TorusGeometry(0.74, 0.07, 6, 16)), body);
  rim.rotation.x = Math.PI / 2;
  rim.position.y = r + 0.24;
  rim.scale.set(1.12, 1.2, 1);
  rig.group.add(rim);
  const stub = new THREE.Mesh(geo("shellTail", () => new THREE.ConeGeometry(0.11, 0.34, 5)), body);
  stub.rotation.x = Math.PI / 2.2;
  stub.position.set(0, r + 0.12, -0.7);
  rig.group.add(stub);
  rig.tail = stub;
  // patas curtas com pé
  const legGeo = geo("shellLeg", () => new THREE.BoxGeometry(0.2, 0.24, 0.2));
  const footGeo = geo("shellFoot", () => new THREE.BoxGeometry(0.26, 0.1, 0.3));
  for (const [lx, lz] of [[-0.38, 0.4], [0.38, 0.4], [-0.38, -0.4], [0.38, -0.4]]) {
    const leg = new THREE.Mesh(legGeo, body);
    leg.position.set(lx, r - 0.05, lz);
    rig.group.add(leg);
    rig.legs.push(leg);
    const foot = new THREE.Mesh(footGeo, body);
    foot.position.set(lx, r - 0.16, lz + 0.04);
    rig.group.add(foot);
  }
  rig.height = r + 1.0;
}

function buildWarden(def: EnemyDef, rig: CreatureRig): void {
  // Shard Warden: Elite — silhueta imponente, cristais grandes, ornamentos, presença
  const r = def.radius;
  const body = stdMat(def.color, { ei: 0.35, rough: 0.6 });
  rig.bodyMats.push(body);
  const stone = stdMat(def.crystalColor ? 0x3a4d74 : 0x3a4d74, { ei: 0.1, rough: 0.9 });
  rig.bodyMats.push(stone);
  const cm = crystalMat(def.crystalColor ?? 0x7cf0ff);
  rig.crystalMats.push(cm);

  // tronco vertical imponente
  const core = new THREE.Mesh(geo("wardenBody", () => new THREE.CylinderGeometry(0.55, 0.85, 1.7, 6)), body);
  core.position.y = r + 0.95;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;
  // ombros largos
  const shoulders = new THREE.Mesh(geo("wardenShoulder", () => new THREE.BoxGeometry(1.9, 0.4, 0.8)), stone);
  shoulders.position.y = r + 1.6;
  rig.group.add(shoulders);
  // cabeça encapuzada + coroa de cristal
  const head = new THREE.Group();
  head.position.y = r + 2.0;
  const skull = new THREE.Mesh(geo("wardenHead", () => new THREE.OctahedronGeometry(0.4, 0)), body);
  head.add(skull);
  addEyes(head, 0.09, 0.3, 0.05, 0x7cf0ff, 0.18);
  rig.group.add(head);
  rig.head = head;
  // coroa/costas: cristais grandes (silhueta)
  const bigGeo = geo("wardenBigCry", () => new THREE.OctahedronGeometry(0.4, 0));
  for (const [cx, cy, cz, s] of [[0, 2.5, -0.3, 1.2], [-0.5, 2.2, -0.25, 0.9], [0.5, 2.2, -0.25, 0.9], [-0.9, 1.7, -0.1, 0.8], [0.9, 1.7, -0.1, 0.8]]) {
    const c = new THREE.Mesh(bigGeo, cm);
    c.position.set(cx, r + cy, cz);
    c.scale.setScalar(s);
    rig.group.add(c);
  }
  // 2 pernas largas
  const legGeo = geo("wardenLeg", () => new THREE.BoxGeometry(0.42, 0.9, 0.45));
  for (const lx of [-0.42, 0.42]) {
    const leg = new THREE.Mesh(legGeo, body);
    leg.position.set(lx, r + 0.1, 0);
    leg.castShadow = true;
    rig.group.add(leg);
    rig.legs.push(leg);
  }
  // braços com garra de cristal (+ 10.5N: ombreiras, manto e cinto de runas)
  const armGeo = geo("wardenArm", () => new THREE.BoxGeometry(0.25, 1.0, 0.25));
  const padGeo = geo("wardenPad", () => new THREE.OctahedronGeometry(0.34, 0));
  for (const ax of [-0.95, 0.95]) {
    const arm = new THREE.Mesh(armGeo, body);
    arm.position.set(ax, r + 1.1, 0.1);
    rig.group.add(arm);
    const claw = new THREE.Mesh(geo("wardenClaw", () => new THREE.ConeGeometry(0.16, 0.4, 4)), cm);
    claw.position.set(ax, r + 0.55, 0.1);
    rig.group.add(claw);
    const pad = new THREE.Mesh(padGeo, stone);
    pad.position.set(ax, r + 1.68, 0);
    pad.scale.set(1, 0.7, 1);
    rig.group.add(pad);
  }
  const cape = new THREE.Mesh(geo("wardenCape", () => new THREE.BoxGeometry(1.5, 1.7, 0.14)), stone);
  cape.position.set(0, r + 1.0, -0.55);
  cape.rotation.x = -0.08;
  rig.group.add(cape);
  const belt = new THREE.Mesh(geo("wardenBelt", () => new THREE.TorusGeometry(0.72, 0.09, 6, 14)), cm);
  belt.rotation.x = Math.PI / 2;
  belt.position.y = r + 0.42;
  rig.group.add(belt);
  rig.height = r + 2.9;
}

// ==================== FASE 10: TEMPEST REACH ====================

function buildStrider(def: EnemyDef, rig: CreatureRig): void {
  // Galestrider: bípede leve e VELOZ — pernas LONGAS, corpo aerodinâmico baixo/alongado,
  // fins de cristal varridos para trás, cabeça pontuda à frente.
  const r = def.radius;
  const body = stdMat(def.color, { ei: 0.4, rough: 0.5 });
  rig.bodyMats.push(body);
  const cm = crystalMat(def.crystalColor ?? 0x9fe6ff);
  rig.crystalMats.push(cm);

  // corpo aerodinâmico (octaedro esticado, inclinado para frente)
  const core = new THREE.Mesh(geo("striderBody", () => new THREE.OctahedronGeometry(0.5, 0)), body);
  core.scale.set(0.7, 0.6, 1.7);
  core.position.set(0, r + 0.85, 0.1);
  core.rotation.x = 0.2;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;

  // cabeça pontuda à frente
  const head = new THREE.Group();
  head.position.set(0, r + 0.95, 0.85);
  const skull = new THREE.Mesh(geo("striderHead", () => new THREE.ConeGeometry(0.22, 0.6, 5)), body);
  skull.rotation.x = Math.PI / 2;
  head.add(skull);
  addEyes(head, 0.06, 0.22, 0.08, 0x9fe6ff, 0.13);
  rig.group.add(head);
  rig.head = head;

  // 2 pernas LONGAS traseiras (correr) + 2 dianteiras menores (leitura de velocidade)
  const backLeg = geo("striderBackLeg", () => new THREE.BoxGeometry(0.13, 1.0, 0.13));
  for (const lx of [-0.26, 0.26]) {
    const leg = new THREE.Mesh(backLeg, body);
    leg.position.set(lx, r + 0.15, -0.35);
    leg.castShadow = true;
    rig.group.add(leg);
    rig.legs.push(leg);
  }
  const frontLeg = geo("striderFrontLeg", () => new THREE.BoxGeometry(0.1, 0.7, 0.1));
  for (const lx of [-0.2, 0.2]) {
    const leg = new THREE.Mesh(frontLeg, body);
    leg.position.set(lx, r + 0.05, 0.5);
    rig.group.add(leg);
    rig.legs.push(leg);
  }
  // fins de cristal varridos para trás (aerodinâmica)
  const finGeo = geo("striderFin", () => new THREE.ConeGeometry(0.12, 0.7, 4));
  for (const [fx, fs] of [[-0.22, 1], [0.22, 1], [0, 1.25]]) {
    const fin = new THREE.Mesh(finGeo, cm);
    fin.position.set(fx, r + 1.15, -0.4);
    fin.rotation.x = -1.9; // varrido para trás
    fin.scale.setScalar(fs);
    rig.group.add(fin);
  }
  // cauda fina
  const tail = new THREE.Mesh(geo("striderTail", () => new THREE.ConeGeometry(0.1, 0.8, 4)), cm);
  tail.rotation.x = -Math.PI / 2.2;
  tail.position.set(0, r + 0.85, -0.85);
  rig.group.add(tail);
  rig.tail = tail;
  rig.height = r + 1.5;
}

function buildSpark(def: EnemyDef, rig: CreatureRig): void {
  // Arcbeak: criatura AÉREA energética — núcleo compacto, asas ANGULARES, bico/cristal frontal.
  const r = def.radius;
  const body = stdMat(def.color, { ei: 0.55, rough: 0.4 });
  rig.bodyMats.push(body);
  const cm = crystalMat(def.crystalColor ?? 0xbfefff);
  rig.crystalMats.push(cm);

  const floatY = r + 1.7;
  const core = new THREE.Mesh(geo("sparkBody", () => new THREE.IcosahedronGeometry(0.34, 0)), body);
  core.position.y = floatY;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;
  // bico/cristal frontal
  const beak = new THREE.Mesh(geo("sparkBeak", () => new THREE.ConeGeometry(0.18, 0.6, 4)), cm);
  beak.rotation.x = Math.PI / 2;
  beak.position.set(0, floatY, 0.45);
  rig.group.add(beak);
  // 2 asas angulares (planos triangulares, não pétalas)
  const wingGeo = geo("sparkWing", () => {
    const s = new THREE.Shape();
    s.moveTo(0, 0); s.lineTo(1.0, 0.15); s.lineTo(0.7, -0.5); s.lineTo(0, 0);
    return new THREE.ShapeGeometry(s);
  });
  for (const side of [-1, 1]) {
    const wing = new THREE.Group();
    wing.position.set(0, floatY, 0);
    const w = new THREE.Mesh(wingGeo, cm);
    w.scale.x = side;
    w.rotation.y = side < 0 ? Math.PI : 0;
    wing.add(w);
    rig.group.add(wing);
    rig.wings.push(wing);
  }
  // cauda curta de energia
  const tail = new THREE.Mesh(geo("sparkTail", () => new THREE.ConeGeometry(0.1, 0.5, 4)), cm);
  tail.position.set(0, floatY - 0.35, -0.35);
  tail.rotation.x = -0.6;
  rig.group.add(tail);
  rig.tail = tail;
  addEyes(core, 0.05, 0.26, 0.05, 0xffffff, 0.1);
  rig.height = floatY + 0.3;
}

function buildBulwark(def: EnemyDef, rig: CreatureRig): void {
  // Stormback: quadrúpede PESADO com carapaça condutora — corpo largo/baixo, domo com
  // cristais, pernas grossas, + bolha de Static Shield (oculta por padrão).
  const r = def.radius;
  const body = stdMat(def.color, { ei: 0.18, rough: 0.95 });
  rig.bodyMats.push(body);
  const stone = stdMat(0x6d7f97, { ei: 0.05, rough: 1 });
  rig.bodyMats.push(stone);
  const cm = crystalMat(def.crystalColor ?? 0x8fd8ff);
  rig.crystalMats.push(cm);

  const core = new THREE.Mesh(geo("bulwarkBody", () => new THREE.BoxGeometry(1.5, 0.7, 1.4)), body);
  core.position.y = r + 0.2;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;
  // carapaça (domo largo)
  const dome = new THREE.Mesh(geo("bulwarkDome", () => new THREE.SphereGeometry(0.95, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2)), stone);
  dome.position.y = r + 0.45;
  dome.scale.set(1.15, 0.85, 1.1);
  dome.castShadow = true;
  rig.group.add(dome);
  // cristais condutores na carapaça (ponto exposto durante o escudo)
  const spikeGeo = geo("bulwarkSpike", () => new THREE.ConeGeometry(0.2, 0.6, 5));
  for (const [px, pz] of [[0, 0], [-0.45, -0.3], [0.45, -0.3], [0, 0.45]]) {
    const p = new THREE.Mesh(spikeGeo, cm);
    p.position.set(px, r + 1.0, pz);
    rig.group.add(p);
  }
  // cabeça baixa
  const head = new THREE.Mesh(geo("bulwarkHead", () => new THREE.BoxGeometry(0.5, 0.4, 0.5)), body);
  head.position.set(0, r + 0.3, 0.85);
  rig.group.add(head);
  addEyes(head, 0.07, 0.26, 0.02, 0xffd24a, 0.15);
  rig.head = head;
  // 4 pernas grossas
  const legGeo = geo("bulwarkLeg", () => new THREE.BoxGeometry(0.34, 0.5, 0.34));
  for (const [lx, lz] of [[-0.55, 0.5], [0.55, 0.5], [-0.55, -0.5], [0.55, -0.5]]) {
    const leg = new THREE.Mesh(legGeo, body);
    leg.position.set(lx, r - 0.05, lz);
    leg.castShadow = true;
    rig.group.add(leg);
    rig.legs.push(leg);
  }
  // bolha de Static Shield (translúcida, oculta por padrão)
  const shieldMat = new THREE.MeshBasicMaterial({ color: def.crystalColor ?? 0x8fd8ff, transparent: true, opacity: 0.22, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
  const shield = new THREE.Mesh(geo("bulwarkShield", () => new THREE.IcosahedronGeometry(1.35, 1)), shieldMat);
  shield.position.y = r + 0.4;
  shield.visible = false;
  rig.group.add(shield);
  rig.shield = shield;
  rig.height = r + 1.4;
}

function buildStalker(def: EnemyDef, rig: CreatureRig): void {
  // Cloudstalker: predador de EMBOSCADA — corpo esguio/baixo, patas finas, olhos/cristais
  // que acendem no telegraph. Materiais com transparência (fade parcial, nunca invisível).
  const r = def.radius;
  const body = stdMat(def.color, { ei: 0.3, rough: 0.5 });
  body.transparent = true; // fade parcial (ambush)
  rig.bodyMats.push(body);
  const cm = crystalMat(def.crystalColor ?? 0xa9c6ff);
  cm.transparent = true;
  rig.crystalMats.push(cm);

  const core = new THREE.Mesh(geo("stalkerBody", () => new THREE.OctahedronGeometry(0.5, 0)), body);
  core.scale.set(0.75, 0.55, 1.6);
  core.position.y = r + 0.5;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;
  // cabeça baixa à frente + olhos brilhantes (telegraph)
  const head = new THREE.Group();
  head.position.set(0, r + 0.5, 0.8);
  const skull = new THREE.Mesh(geo("stalkerHead", () => new THREE.BoxGeometry(0.4, 0.3, 0.45)), body);
  head.add(skull);
  addEyes(head, 0.07, 0.24, 0.05, 0xbfe0ff, 0.14);
  rig.group.add(head);
  rig.head = head;
  // cristais dorsais (acendem no telegraph)
  const finGeo = geo("stalkerFin", () => new THREE.ConeGeometry(0.13, 0.5, 4));
  for (const sz of [0.2, -0.15, -0.5]) {
    const f = new THREE.Mesh(finGeo, cm);
    f.position.set(0, r + 0.85, sz);
    f.rotation.x = -0.3;
    rig.group.add(f);
  }
  // 4 patas finas
  const legGeo = geo("stalkerLeg", () => new THREE.BoxGeometry(0.1, 0.55, 0.1));
  for (const [lx, lz] of [[-0.26, 0.5], [0.26, 0.5], [-0.26, -0.5], [0.26, -0.5]]) {
    const leg = new THREE.Mesh(legGeo, body);
    leg.position.set(lx, r + 0.0, lz);
    rig.group.add(leg);
    rig.legs.push(leg);
  }
  const tail = new THREE.Mesh(geo("stalkerTail", () => new THREE.ConeGeometry(0.1, 0.9, 4)), cm);
  tail.rotation.x = -Math.PI / 2.1;
  tail.position.set(0, r + 0.55, -0.85);
  rig.group.add(tail);
  rig.tail = tail;
  rig.height = r + 1.15;
}

function buildSentinel(def: EnemyDef, rig: CreatureRig): void {
  // Tempest Warden: Elite ALTO e imponente — placas condutoras, cristais flutuantes,
  // arcos de energia. Silhueta própria (≠ Shard Warden): mais angular e vertical.
  const r = def.radius;
  const body = stdMat(def.color, { ei: 0.35, rough: 0.55 });
  rig.bodyMats.push(body);
  const stone = stdMat(0x6c86a8, { ei: 0.1, rough: 0.9 });
  rig.bodyMats.push(stone);
  const cm = crystalMat(def.crystalColor ?? 0x9fe0ff);
  rig.crystalMats.push(cm);

  // tronco alto e angular (octaedro esticado)
  const core = new THREE.Mesh(geo("sentinelBody", () => new THREE.OctahedronGeometry(0.7, 0)), body);
  core.scale.set(0.8, 1.9, 0.8);
  core.position.y = r + 1.3;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;
  // placas condutoras (ombros angulares)
  const shoulders = new THREE.Mesh(geo("sentinelShoulder", () => new THREE.BoxGeometry(2.1, 0.35, 0.7)), stone);
  shoulders.position.y = r + 2.0;
  shoulders.rotation.z = 0.06;
  rig.group.add(shoulders);
  // cabeça angular + olhos
  const head = new THREE.Group();
  head.position.y = r + 2.45;
  const skull = new THREE.Mesh(geo("sentinelHead", () => new THREE.ConeGeometry(0.35, 0.7, 5)), body);
  head.add(skull);
  addEyes(head, 0.08, 0.2, 0.1, 0x9fe0ff, 0.16);
  rig.group.add(head);
  rig.head = head;
  // cristais FLUTUANTES ao redor (silhueta + wings p/ leve bob)
  const shardGeo = geo("sentinelShard", () => new THREE.OctahedronGeometry(0.32, 0));
  for (const [cx, cy, cz, s] of [[-1.0, 1.9, 0.2, 1.0], [1.0, 2.1, 0.2, 1.1], [-0.8, 2.6, -0.2, 0.8], [0.8, 2.7, -0.2, 0.9]]) {
    const shard = new THREE.Group();
    shard.position.set(cx, r + cy, cz);
    const c = new THREE.Mesh(shardGeo, cm);
    c.scale.setScalar(s);
    shard.add(c);
    rig.group.add(shard);
    rig.wings.push(shard); // reaproveita o slot 'wings' para o bob dos cristais
  }
  // 2 pernas altas
  const legGeo = geo("sentinelLeg", () => new THREE.BoxGeometry(0.4, 1.1, 0.42));
  for (const lx of [-0.45, 0.45]) {
    const leg = new THREE.Mesh(legGeo, body);
    leg.position.set(lx, r + 0.2, 0);
    leg.castShadow = true;
    rig.group.add(leg);
    rig.legs.push(leg);
  }
  rig.height = r + 3.1;
}

// ---- silhuetas herdadas (Fase 1-3) levemente melhoradas ----
function buildOrb(def: EnemyDef, rig: CreatureRig): void {
  const body = stdMat(def.color, { ei: 0.35 });
  rig.bodyMats.push(body);
  const core = new THREE.Mesh(geo("orbBody", () => new THREE.IcosahedronGeometry(def.radius, 0)), body);
  core.position.y = def.radius + 0.1;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;
  addEyes(core, def.radius * 0.16, def.radius * 0.85, def.radius * 0.25, 0xff4d6d, def.radius * 0.32);
  // pequenas farpas (menos "esfera crua")
  const cm = crystalMat(def.crystalColor ?? 0xc9b6ff);
  rig.crystalMats.push(cm);
  for (const a of [0.6, 2.1, 4.0]) {
    const s = new THREE.Mesh(geo("orbSpike", () => new THREE.ConeGeometry(0.1, 0.3, 4)), cm);
    s.position.set(Math.cos(a) * def.radius * 0.7, def.radius + 0.4, Math.sin(a) * def.radius * 0.7);
    rig.group.add(s);
  }
  rig.height = def.radius * 2 + 0.4;
}
function buildRunner(def: EnemyDef, rig: CreatureRig): void {
  const body = stdMat(def.color, { ei: 0.35 });
  rig.bodyMats.push(body);
  const core = new THREE.Mesh(geo("runnerBody", () => new THREE.OctahedronGeometry(def.radius, 0)), body);
  core.scale.set(0.7, 0.7, 1.9);
  core.position.y = def.radius + 0.25;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;
  addEyes(core, def.radius * 0.14, def.radius * 1.5, def.radius * 0.2, 0xff4d6d, def.radius * 0.3);
  // 2 patas insinuadas
  const legGeo = geo("runnerLeg", () => new THREE.BoxGeometry(0.1, 0.3, 0.1));
  for (const lx of [-0.18, 0.18]) {
    const leg = new THREE.Mesh(legGeo, body);
    leg.position.set(lx, def.radius - 0.05, 0);
    rig.group.add(leg);
    rig.legs.push(leg);
  }
  rig.height = def.radius * 2 + 0.5;
}
function buildBrute(def: EnemyDef, rig: CreatureRig): void {
  const body = stdMat(def.color, { ei: 0.3, rough: 0.8 });
  rig.bodyMats.push(body);
  const core = new THREE.Mesh(geo("bruteBody", () => new THREE.BoxGeometry(def.radius * 2, def.radius * 1.7, def.radius * 1.9)), body);
  core.position.y = def.radius + 0.1;
  core.castShadow = true;
  rig.group.add(core);
  rig.core = core;
  addEyes(core, def.radius * 0.16, def.radius * 0.9, def.radius * 0.45, 0xff4d6d, def.radius * 0.32);
  // ombros
  const cm = crystalMat(def.crystalColor ?? 0xff8a5a);
  rig.crystalMats.push(cm);
  for (const sx of [-def.radius, def.radius]) {
    const s = new THREE.Mesh(geo("bruteSpike", () => new THREE.ConeGeometry(0.2, 0.5, 4)), cm);
    s.position.set(sx, def.radius * 1.6, 0);
    rig.group.add(s);
  }
  // 4 pernas curtas
  const legGeo = geo("bruteLeg", () => new THREE.BoxGeometry(0.3, 0.35, 0.3));
  for (const [lx, lz] of [[-def.radius * 0.6, def.radius * 0.6], [def.radius * 0.6, def.radius * 0.6], [-def.radius * 0.6, -def.radius * 0.6], [def.radius * 0.6, -def.radius * 0.6]]) {
    const leg = new THREE.Mesh(legGeo, body);
    leg.position.set(lx, def.radius - 0.15, lz);
    rig.group.add(leg);
    rig.legs.push(leg);
  }
  rig.height = def.radius * 2.3;
}

const BUILDERS: Record<EnemyShape, (def: EnemyDef, rig: CreatureRig) => void> = {
  orb: buildOrb,
  runner: buildRunner,
  brute: buildBrute,
  beast: buildBeast,
  heavy: buildHeavy,
  flyer: buildFlyer,
  shell: buildShell,
  warden: buildWarden,
  // Fase 10 — Tempest Reach
  strider: buildStrider,
  spark: buildSpark,
  bulwark: buildBulwark,
  stalker: buildStalker,
  sentinel: buildSentinel,
};

// 10.5K — pele por silhueta (ver CreatureSkins): quitina/escamas/rocha/couro/cristal
const SHAPE_SKIN: Record<EnemyShape, SkinKind> = {
  orb: "chitin", runner: "scales", brute: "stone", beast: "scales", heavy: "stone", flyer: "crystal",
  shell: "chitin", warden: "stone", strider: "hide", spark: "crystal", bulwark: "stone", stalker: "hide", sentinel: "stone",
};

// sombra de contato compartilhada (blob barato) — evita sensação de "flutuando"
// 10.6B: era um disco preto CHAPADO (lia como adesivo). Agora tem degradê radial: escuro
// no centro, some na borda — assenta a criatura no chão sem parecer recorte.
function makeBlobTexture(): THREE.CanvasTexture {
  const S = 64;
  const c = document.createElement("canvas");
  c.width = c.height = S;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(S / 2, S / 2, 1, S / 2, S / 2, S / 2);
  g.addColorStop(0, "#ffffff");
  g.addColorStop(0.42, "#d8d8d8");
  g.addColorStop(0.74, "#5a5a5a");
  g.addColorStop(1, "#000000");
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, S, S);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(S / 2, S / 2, S / 2, 0, Math.PI * 2);
  ctx.fill();
  return new THREE.CanvasTexture(c);
}
const _shadowGeo = new THREE.CircleGeometry(1, 20);
const _shadowMat = new THREE.MeshBasicMaterial({
  color: 0x0a0812, alphaMap: makeBlobTexture(), transparent: true, opacity: 0.46, depthWrite: false,
});
_shadowGeo.userData.shared = true;
_shadowMat.userData.shared = true;

/** Blob shadow no chão (raio proporcional). Reusa geo/material compartilhados. */
export function addContactShadow(parent: THREE.Object3D, radius: number, opacity = 0.46): THREE.Mesh {
  const mat = opacity === 0.46 ? _shadowMat : _shadowMat.clone();
  if (opacity !== 0.46) (mat as THREE.MeshBasicMaterial).opacity = opacity;
  const s = new THREE.Mesh(_shadowGeo, mat);
  s.rotation.x = -Math.PI / 2;
  s.position.y = 0.035;
  s.scale.setScalar(radius);
  parent.add(s);
  return s;
}

/** Constrói o rig da criatura conforme a silhueta (shape) do def. */
export function buildCreature(def: EnemyDef): CreatureRig {
  const rig: CreatureRig = {
    group: new THREE.Group(),
    bodyMats: [],
    crystalMats: [],
    legs: [],
    wings: [],
    head: null,
    tail: null,
    core: null,
    shield: null,
    height: def.radius * 2,
  };
  const look = regionLook(enemyRegion(def.id));
  if (!buildForestCreature(def, rig)) {
    (BUILDERS[def.shape] ?? buildOrb)(def, rig);
    // 10.9BT — PELE POR REGIÃO (antes era por silhueta): o 1º material (corpo, cor do def) usa a
    // pele principal da região; os demais (placas/pedra) a secundária. `def.skin` fixa a pele.
    // Floresta = couro/quitina, Terras Partidas = cristal/escamas, Tempestade = pedra/escamas.
    const big = def.shape === "heavy" || def.shape === "warden" || def.shape === "bulwark" || def.shape === "sentinel";
    rig.bodyMats.forEach((m, i) => {
      const kind: SkinKind = def.skin ?? (i === 0 ? look.skin : look.skin2) ?? SHAPE_SKIN[def.shape];
      applySkin(m, kind, kind === "stone" ? 0.05 : 0.035, big ? 5 : 3);
    });
  }
  // sombra de contato (blob) — maior para criaturas pesadas/altas
  const bigShadow = def.shape === "heavy" || def.shape === "warden" || def.shape === "bulwark" || def.shape === "sentinel";
  addContactShadow(rig.group, def.radius * (bigShadow ? 1.5 : 1.2));
  finishCreature(rig, def, look);
  addVoidSignature(rig, def);
  return rig;
}

const _hsl = { h: 0, s: 0, l: 0 };

/**
 * 10.9BT — ACABAMENTO comum a todas as criaturas (procedurais e da floresta):
 *  - tinta secundária da região misturada no emissivo (veios musgo / facetas ciano / riscos azuis);
 *  - luz de borda na cor da REGIÃO (era a cor do cristal de cada bicho — agora a região se lê de longe);
 *  - variação por instância: escala ±6% e tom ±8% (hue/luminosidade) — sem clones idênticos;
 *  - marcas de ELITE: 2-3 espinhos extras na cor da aura + olhos maiores.
 * Só visual: raio/hitbox/stats não mudam (a escala é no rig, não no grupo do Enemy).
 */
function finishCreature(rig: CreatureRig, def: EnemyDef, look: RegionLook): void {
  const tint = new THREE.Color(look.tint);
  const hueOff = (Math.random() - 0.5) * 0.06; // ±3% de matiz
  const lightOff = (Math.random() - 0.5) * 0.16; // ±8% de luminosidade
  for (const m of rig.bodyMats) {
    m.color.getHSL(_hsl);
    m.color.setHSL((_hsl.h + hueOff + 1) % 1, _hsl.s, THREE.MathUtils.clamp(_hsl.l * (1 + lightOff), 0.04, 0.95));
    m.emissive.copy(m.color).lerp(tint, 0.45); // "veios" da região no emissivo (intensidade baixa já existente)
    applyRim(m, { color: look.rim, power: 2.2, strength: 1.25 });
  }
  rig.group.scale.setScalar(1 + (Math.random() - 0.5) * 0.12); // ±6%

  if (def.elite) {
    applyVeins(rig.bodyMats, def.elite.aura); // 10.9GV — rachaduras brilhando na cor da aura
    // olhos maiores (todos os olhos marcados em addEyes / ForestCreatures.eyes)
    rig.group.traverse((o) => { if (o.userData.eye === true) o.scale.multiplyScalar(1.4); });
    // 2-3 espinhos extras na cor da aura, ao longo das costas
    const auraMat = new THREE.MeshStandardMaterial({ color: def.elite.aura, emissive: def.elite.aura, emissiveIntensity: 1.1, roughness: 0.3, flatShading: true });
    rig.crystalMats.push(auraMat);
    const spikeGeo = geo("eliteSpike", () => new THREE.ConeGeometry(0.09, 0.42, 4));
    const n = def.radius > 0.9 ? 3 : 2;
    for (let i = 0; i < n; i++) {
      const s = new THREE.Mesh(spikeGeo, auraMat);
      const t = i / (n - 1) - 0.5; // -0.5..0.5 ao longo do eixo Z
      s.position.set((i % 2 ? 0.12 : -0.12) * def.radius, rig.height * 0.78, -t * def.radius * 0.9);
      s.rotation.x = -0.35 - t * 0.4;
      s.rotation.z = (i % 2 ? -0.18 : 0.18);
      rig.group.add(s);
    }
  }
}

/**
 * 10.9BT — COROA do CAMPEÃO de território: 3 cones pequenos em leque sobre a cabeça (ou o topo
 * do rig, se não houver cabeça), na cor da aura. Chamado pelo Enemy quando vira campeão.
 */
export function addChampionCrest(rig: CreatureRig, color: number): void {
  applyVeins(rig.bodyMats, color, 1.3); // 10.9GV
  const mat = new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1.2, roughness: 0.3, flatShading: true });
  rig.crystalMats.push(mat);
  const g = geo("champCrest", () => new THREE.ConeGeometry(0.07, 0.34, 4));
  const parent = rig.head ?? rig.group;
  const baseY = rig.head ? 0.22 : rig.height * 0.95;
  for (const [x, rz, sc] of [[-0.16, 0.45, 0.85], [0, 0, 1.15], [0.16, -0.45, 0.85]] as const) {
    const c = new THREE.Mesh(g, mat);
    c.position.set(x, baseY + (sc - 0.85) * 0.2, 0);
    c.rotation.z = rz;
    c.scale.setScalar(sc);
    parent.add(c);
  }
}
